import type { Kmat, LinearCstic } from "~/Configurators/Linear/models";
import { shaftTolerance as inchShaftTolerance } from "~/Configurators/Shaft/inch/inchCsticConstants";
import {
  radialThread,
  shaftTolerance as metricShaftTolerance,
} from "~/Configurators/Shaft/metric/metricCsticConstants";
import { ConfiguratorType } from "~/Configurators/ConfiguratorType";
import type { WithTranslation } from "react-i18next";
import { formatCsticValueAsNumber } from "~/Configurators/Shaft/components/useMinMaxCsticLabel";

const CHAMFERED_CODES = ["005", "007"];

export function showChamferedSection(machiningValue?: string | null) {
  return CHAMFERED_CODES.includes(machiningValue ?? "");
}

const RADIAL_THREAD_CODES = ["980"];
export function showRadialThreadSection(machiningValue?: string | null) {
  return RADIAL_THREAD_CODES.includes(machiningValue ?? "");
}

const AXIAL_THREAD_CODES = ["003", "007"];
export function showAxialThreadSection(machiningValue?: string | null) {
  return AXIAL_THREAD_CODES.includes(machiningValue ?? "");
}

export function isChamferManualWidth(chamferAngleValue?: string | null) {
  return !!chamferAngleValue?.toUpperCase().startsWith("SF");
}

export function resolveShaftToleranceMinmax(
  configuratorType: ConfiguratorType.InchShaft | ConfiguratorType.MetricShaft,
  shaftToleranceValue: string | undefined | null,
  cstics: Dict<LinearCstic>,
  unit = "",
) {
  if (!shaftToleranceValue) {
    return undefined;
  }

  const shaftTolerance = configuratorType === ConfiguratorType.InchShaft ? inchShaftTolerance : metricShaftTolerance;

  let csticName = undefined;
  if (shaftToleranceValue === "LN") {
    csticName = shaftTolerance.minmax.ln;
  } else if (shaftToleranceValue === "LS") {
    csticName = shaftTolerance.minmax.ls;
  }

  if (!csticName) {
    return undefined;
  }

  const minmaxCstic = cstics[csticName];
  return formatShaftToleranceMinmax(minmaxCstic?.value, unit, shaftTolerance.maxDecimal);
}

export function formatShaftToleranceMinmax(minmaxValue: string | undefined | null, unit = "", maxDecimalDigits = 2) {
  const val = formatCsticValueAsNumber(minmaxValue, maxDecimalDigits);
  return minmaxValue ? `± ${val}${unit}` : undefined;
}

export type ValidationMode =
  | "full" // Performs a full validation, failing if a required field is empty
  | "feedback"; // Performs a quick validation, skipping non-dirty (pristine) fields;

export function resolveMachiningLabelString(
  configurator: ConfiguratorType.InchShaft | ConfiguratorType.MetricShaft,
  t: WithTranslation["t"],
  value: string,
) {
  let key: string = `configurator.shaft.summary.machining.${value}`;
  const translation = t(key, { returnObjects: true }) as string | { inch: string; metric: string };
  if (typeof translation !== "string") {
    key = configurator === ConfiguratorType.InchShaft ? translation.inch : translation.metric;
  }
  return t(key);
}

type ErrorType = "required" | "invalid";
export type ShaftMachiningExtraError = {
  type: ErrorType;
  cstic: string;
};

type ValidationContext = {
  kmat: Kmat;
  conflictedCstics: string[];
  mode: ValidationMode;
};

export function validateChamferSegment(
  csticConfig: {
    angle: string;
    width: {
      sf: string;
      nf: string;
    };
  },
  { kmat, conflictedCstics, mode }: ValidationContext,
) {
  let errors: ShaftMachiningExtraError[] = [];
  const cstics = kmat.cstics;
  const angleValue = cstics[csticConfig.angle]?.value;

  if (!angleValue) {
    errors.push({ type: "required", cstic: csticConfig.angle });
  } else if (conflictedCstics.includes(csticConfig.angle)) {
    errors.push({ type: "invalid", cstic: csticConfig.angle });
  }
  if (isChamferManualWidth(angleValue)) {
    const widthValue = cstics[csticConfig.width.sf]?.value;
    if (!widthValue) {
      errors.push({ type: "required", cstic: csticConfig.width.sf });
    } else if (conflictedCstics.includes(csticConfig.width.sf)) {
      errors.push({ type: "invalid", cstic: csticConfig.width.sf });
    }
  }

  if (mode === "feedback") {
    errors = errors.filter((error) => {
      return error.type !== "required";
    });
  }

  return errors;
}

export function validateAxialThread(
  csticConfig: {
    size: string;
    depth: string;
  },
  { kmat, conflictedCstics, mode }: ValidationContext,
) {
  let errors: ShaftMachiningExtraError[] = [];
  const cstics = kmat.cstics;
  const sizeValue = cstics[csticConfig.size]?.value;
  if (!sizeValue) {
    errors.push({ type: "required", cstic: csticConfig.size });
  } else if (conflictedCstics.includes(csticConfig.size)) {
    errors.push({ type: "invalid", cstic: csticConfig.size });
  }

  if (mode === "feedback") {
    errors = errors.filter((error) => {
      return error.type !== "required";
    });
  }

  return errors;
}

export function isRadialFullyConfiguredInch(kmat: Kmat) {
  return true;
}

export function validateRadialThreadMetric({ kmat, conflictedCstics, mode }: ValidationContext) {
  let errors: ShaftMachiningExtraError[] = [];

  const cstics = kmat.cstics;
  const hasSize = !!cstics[radialThread.size]?.value;

  if (!hasSize) {
    errors.push({
      type: "required",
      cstic: radialThread.size,
    });
  }
  const threadSpacingMode = cstics[radialThread.threadSpacing.mode]?.value;
  let spacingCstic;

  if (threadSpacingMode === radialThread.threadSpacing.modeRecommended) {
    spacingCstic = cstics[radialThread.threadSpacing.recommended];
    const hasSpacing = !!spacingCstic?.value;
    if (!hasSpacing) {
      errors.push({
        type: "required",
        cstic: radialThread.threadSpacing.recommended,
      });
    }
  } else {
    spacingCstic = cstics[radialThread.threadSpacing.individual.value];
    const hasSpacing = !!spacingCstic?.value;
    if (!hasSpacing) {
      errors.push({
        type: "required",
        cstic: radialThread.threadSpacing.individual.value,
      });
    } else if (conflictedCstics.includes(radialThread.threadSpacing.individual.value)) {
      errors.push({
        type: "invalid",
        cstic: radialThread.threadSpacing.individual.value,
      });
    }
  }

  ([radialThread.aL.value, radialThread.aR.value] as const).forEach((csticName) => {
    const cstic = cstics[csticName];
    if (!cstic?.value) {
      errors.push({
        type: "required",
        cstic: csticName,
      });
    } else if (conflictedCstics.includes(csticName)) {
      errors.push({
        type: "invalid",
        cstic: csticName,
      });
    }
  });

  if (mode === "feedback") {
    errors = errors.filter((error) => {
      return error.type !== "required";
    });
  }

  return errors;
}
